import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Tabs,
  Tab,
  Typography,
  Button,
  Chip,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  Paper
} from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { debugLogger } from '../../Helpers/debugLogger';
import { DEBUG_CATEGORIES, CATEGORY_DESCRIPTIONS } from '../../Helpers/debugCategories';
import { DEBUG_ENABLED, flushDebugLog, clearDebugLog, debugLogStatus } from '../../Helpers/debugCapture';
import { useAuth } from '../../Contexts/AuthContext';

const DebugPanel = () => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(0);
  const [logs, setLogs] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [captureStatus, setCaptureStatus] = useState({ count: 0, session: null });
  const [sessionInput, setSessionInput] = useState('');
  const [flushResult, setFlushResult] = useState(null);
  const auth = useAuth();

  // Refresh logs
  const refreshLogs = () => {
    const allLogs = debugLogger.getLogs();
    setLogs(allLogs);
  };

  // Auto-refresh logs when panel is open
  useEffect(() => {
    if (open && autoRefresh) {
      const interval = setInterval(() => {
        refreshLogs();
        if (DEBUG_ENABLED) setCaptureStatus(debugLogStatus());
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [open, autoRefresh]);

  // Initial load
  useEffect(() => {
    if (open) {
      refreshLogs();
    }
  }, [open]);

  // Only render in non-production environments
  if (import.meta.env.VITE_ENV === 'production') {
    return null;
  }

  const handleExport = () => {
    debugLogger.exportLogs();
  };

  const handleClear = () => {
    debugLogger.clear();
    refreshLogs();
  };

  // Filter logs
  const filteredLogs = logs.filter(log => {
    const matchesCategory = selectedCategory === 'all' || log.category === selectedCategory;
    const matchesSearch = !searchTerm || 
      log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      JSON.stringify(log.data).toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Get all available categories (from DEBUG_CATEGORIES) plus any used categories
  const usedCategories = debugLogger.getCategories();
  const allAvailableCategories = ['all', ...Object.values(DEBUG_CATEGORIES).sort()];
  
  const stats = debugLogger.getStats();

  return (
    <>
      {/* Floating Debug Button */}
      <IconButton
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          zIndex: 9999,
          bgcolor: 'primary.main',
          color: 'white',
          '&:hover': {
            bgcolor: 'primary.dark',
          },
          boxShadow: 3
        }}
        onClick={() => setOpen(true)}
        title="Open Debug Panel"
      >
        <BugReportIcon />
      </IconButton>

      {/* Debug Panel Drawer */}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        sx={{
          zIndex: 9999,
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 600 },
            zIndex: 9999,
          }
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BugReportIcon color="primary" />
              <Typography variant="h6">Debug Panel</Typography>
              <Chip 
                label={import.meta.env.VITE_ENV || 'development'} 
                size="small" 
                color="primary" 
              />
            </Box>
            <IconButton onClick={() => setOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Tabs */}
          <Tabs 
            value={tab} 
            onChange={(e, v) => setTab(v)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label={`Logs (${logs.length})`} />
            <Tab label="Stats" />
            {DEBUG_ENABLED && <Tab label={`Capture (${captureStatus.count})`} />}
            <Tab label="Help" />
          </Tabs>

          {/* Tab Content */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {/* Logs Tab */}
            {tab === 0 && (
              <LogsTab
                logs={filteredLogs}
                allAvailableCategories={allAvailableCategories}
                selectedCategory={selectedCategory}
                setSelectedCategory={setSelectedCategory}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                autoRefresh={autoRefresh}
                setAutoRefresh={setAutoRefresh}
                onRefresh={refreshLogs}
                onExport={handleExport}
                onClear={handleClear}
              />
            )}

            {/* Stats Tab */}
            {tab === 1 && <StatsTab stats={stats} />}

            {/* Capture Tab (debug mode only) */}
            {DEBUG_ENABLED && tab === 2 && (
              <CaptureTab
                status={captureStatus}
                sessionInput={sessionInput}
                setSessionInput={setSessionInput}
                flushResult={flushResult}
                onFlush={async () => {
                  setFlushResult(null);
                  const token = auth?.activeUser?.accessToken;
                  await flushDebugLog(sessionInput || undefined, token);
                  setCaptureStatus(debugLogStatus());
                  setFlushResult(`Flushed to session: ${sessionInput || '(auto)'}`);
                }}
                onClear={() => {
                  clearDebugLog();
                  setCaptureStatus(debugLogStatus());
                  setFlushResult(null);
                }}
              />
            )}

            {/* Help Tab */}
            {tab === (DEBUG_ENABLED ? 3 : 2) && <HelpTab />}
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

// Logs Tab Component
const LogsTab = ({ 
  logs, 
  allAvailableCategories,
  selectedCategory, 
  setSelectedCategory,
  searchTerm,
  setSearchTerm,
  autoRefresh,
  setAutoRefresh,
  onRefresh,
  onExport,
  onClear
}) => (
  <>
    {/* Controls */}
    <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          size="small"
          startIcon={<RefreshIcon />}
          onClick={onRefresh}
          variant="outlined"
        >
          Refresh
        </Button>
        <Button
          size="small"
          startIcon={<DownloadIcon />}
          onClick={onExport}
          variant="outlined"
        >
          Export
        </Button>
        <Button
          size="small"
          startIcon={<DeleteIcon />}
          onClick={onClear}
          variant="outlined"
          color="error"
        >
          Clear
        </Button>
        <Button
          size="small"
          onClick={() => setAutoRefresh(!autoRefresh)}
          variant={autoRefresh ? 'contained' : 'outlined'}
        >
          Auto-refresh: {autoRefresh ? 'ON' : 'OFF'}
        </Button>
      </Box>

      <TextField
        size="small"
        label="Search logs"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        fullWidth
      />

      <FormControl size="small" fullWidth>
        <InputLabel>Category</InputLabel>
        <Select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          label="Category"
        >
          {allAvailableCategories.map(cat => (
            <MenuItem key={cat} value={cat}>
              {cat === 'all' ? 'All Categories' : cat}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>

    <Divider sx={{ mb: 2 }} />

    {/* Logs List */}
    {logs.length === 0 ? (
      <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
        No logs to display
      </Typography>
    ) : (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {logs.map((log, index) => (
          <LogEntry key={index} log={log} />
        ))}
      </Box>
    )}
  </>
);

// Individual Log Entry Component
const LogEntry = ({ log }) => {
  const [expanded, setExpanded] = useState(false);

  const getLevelColor = (level) => {
    switch (level) {
      case 'error': return 'error.main';
      case 'warn': return 'warning.main';
      default: return 'info.main';
    }
  };

  return (
    <Paper 
      sx={{ 
        p: 1.5, 
        cursor: 'pointer',
        '&:hover': { bgcolor: 'action.hover' },
        borderLeft: 3,
        borderColor: getLevelColor(log.level)
      }}
      onClick={() => setExpanded(!expanded)}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Chip label={log.category} size="small" color="primary" />
          <Typography variant="caption" color="text.secondary">
            {log.time}
          </Typography>
        </Box>
      </Box>
      
      <Typography variant="body2" sx={{ mb: 1 }}>
        {log.message}
      </Typography>

      {expanded && (
        <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1, overflow: 'auto' }}>
          <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {JSON.stringify(log.data || log.error, null, 2)}
          </Typography>
          {log.stack && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {log.stack}
            </Typography>
          )}
        </Box>
      )}
    </Paper>
  );
};

// Stats Tab Component
const StatsTab = ({ stats }) => (
  <Box>
    <Typography variant="h6" gutterBottom>Statistics</Typography>
    
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>Total Logs</Typography>
      <Typography variant="h4">{stats.total}</Typography>
    </Paper>

    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>By Level</Typography>
      <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
        <Chip label={`Info: ${stats.byLevel.info}`} color="info" />
        <Chip label={`Warn: ${stats.byLevel.warn}`} color="warning" />
        <Chip label={`Error: ${stats.byLevel.error}`} color="error" />
      </Box>
    </Paper>

    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>By Category</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
        {Object.entries(stats.byCategory).map(([category, count]) => (
          <Box key={category} sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">{category}</Typography>
            <Chip label={count} size="small" />
          </Box>
        ))}
      </Box>
    </Paper>
  </Box>
);

// Capture Tab — only shown when ?debug=1 is in the URL
const CaptureTab = ({ status, sessionInput, setSessionInput, flushResult, onFlush, onClear }) => (
  <Box>
    <Typography variant="h6" gutterBottom>Calculation Capture</Typography>

    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>Buffer Status</Typography>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
        <Chip label={`${status.count} entries`} color={status.count > 0 ? 'success' : 'default'} />
        {status.session && <Chip label={`session: ${status.session}`} size="small" />}
      </Box>
    </Paper>

    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>Flush to Backend</Typography>
      <TextField
        size="small"
        label="Session name (optional)"
        placeholder="e.g. alex-test-1"
        value={sessionInput}
        onChange={e => setSessionInput(e.target.value)}
        fullWidth
        sx={{ mb: 1 }}
      />
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="contained"
          startIcon={<CloudUploadIcon />}
          onClick={onFlush}
          disabled={status.count === 0}
        >
          Flush ({status.count})
        </Button>
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={onClear}
          disabled={status.count === 0}
        >
          Clear
        </Button>
      </Box>
      {flushResult && (
        <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
          ✓ {flushResult}
        </Typography>
      )}
    </Paper>

    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>Console Commands</Typography>
      <Typography variant="body2" component="pre" sx={{ bgcolor: 'grey.100', p: 1, borderRadius: 1, fontSize: '0.75rem' }}>
        {`window.flushDebugLog()           // flush with auto session
window.flushDebugLog('my-session') // flush with named session
window.clearDebugLog()             // clear buffer
window.debugLogStatus()            // show buffer info`}
      </Typography>
    </Paper>
  </Box>
);

// Help Tab Component
const HelpTab = () => (  <Box>
    <Typography variant="h6" gutterBottom>Debug Logger Help</Typography>
    
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>Console Access</Typography>
      <Typography variant="body2" component="pre" sx={{ bgcolor: 'grey.100', p: 1, borderRadius: 1, fontSize: '0.75rem' }}>
        {`// Access logger in console
window.debugLogger.getLogs()
window.debugLogger.getLogs('CATEGORY')
window.debugLogger.exportLogs()
window.debugLogger.clear()
window.debugLogger.getStats()`}
      </Typography>
    </Paper>

    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>Usage in Code</Typography>
      <Typography variant="body2" component="pre" sx={{ bgcolor: 'grey.100', p: 1, borderRadius: 1, fontSize: '0.75rem' }}>
        {`import { debugLogger } from 'Helpers/debugLogger';

// Log info
debugLogger.log('CATEGORY', 'Message', { data });

// Log warning
debugLogger.warn('CATEGORY', 'Warning', { data });

// Log error
debugLogger.error('CATEGORY', 'Error', error);`}
      </Typography>
    </Paper>

    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>Available Categories</Typography>
      <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
        {Object.entries(CATEGORY_DESCRIPTIONS).map(([category, description]) => (
          <Box key={category} sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              {category}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {description}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>

    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>Quick Tips</Typography>
      <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
        <li>Logging only works in development and staging environments</li>
        <li>Logs are capped at 1000 entries (oldest removed first)</li>
        <li>Click any log entry to expand and see full details</li>
        <li>Use category filter to focus on specific areas</li>
        <li>Export logs before clearing to save for later analysis</li>
      </Typography>
    </Paper>
  </Box>
);

export default DebugPanel;
